/**
 * @fileoverview Custom glass-morphism toast for new order notifications.
 * Renders rich order details matching the dashboard's visual language.
 * Clicking the toast body navigates to the dashboard with today's date
 * and highlights the new orders in the OrderFeed.
 * @module features/daily-summary/components/OrderNotificationToast
 */

import { ReactElement, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { codeToFlag } from '@/shared/utils/countryUtils'
import { formatMoney } from '@/shared/utils/money'
import { useFilter } from '@/shared/hooks/useFilter'
import { highlightNewOrders } from '../hooks/newOrderHighlights'
import type { OrderGroup } from '../hooks/useTodaysOrders'

const FALLBACK_IMG =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0OCIgaGVpZ2h0PSI0OCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM1YTVhNmEiIHN0cm9rZS13aWR0aD0iMS41Ij48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHJ4PSIyIi8+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiLz48cGF0aCBkPSJNMjEgMTVsLTUtNUw1IDIxIi8+PC9zdmc+'

interface OrderNotificationToastProps {
  toastId: string | number
  newOrders: OrderGroup[]
  currency: string
}

export function OrderNotificationToast({
  toastId,
  newOrders,
  currency,
}: OrderNotificationToastProps): ReactElement {
  const { t } = useTranslation('features-home')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { setDateRange } = useFilter()

  const isSingle = newOrders.length === 1
  const count = newOrders.length
  const totalRevenue = newOrders.reduce((sum, o) => sum + o.total, 0)
  const fmt = (v: number): string => formatMoney(v, currency, undefined, 0, 0)

  const handleClick = useCallback(() => {
    // Set date filter to today so OrderFeed becomes visible
    const now = new Date()
    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    setDateRange(todayMidnight, todayMidnight)

    // Mark orders as "new" for badge highlights in OrderGroupCard
    highlightNewOrders(newOrders.map((o) => o.orderId))

    // Navigate to dashboard, preserving current search params (e.g. ?profit=cm2)
    navigate(`/dashboard?${searchParams.toString()}`)

    // Dismiss the toast
    toast.dismiss(toastId)
  }, [setDateRange, newOrders, navigate, searchParams, toastId])

  return (
    <div className="w-[360px] rounded-2xl border border-border/50 bg-[var(--glass-surface-dark)] backdrop-blur-xl shadow-[var(--shadow-glass-lg)] overflow-hidden">
      {/* Header row: title | dismiss gutter */}
      <div className="grid grid-cols-[1fr_auto]">
        <div className="flex items-center gap-2.5 px-4 py-3">
          {/* Pulsing green dot */}
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground leading-tight">
              {isSingle
                ? t('orderFeed.toastTitle', { count: 1 })
                : t('orderFeed.toastTitle', { count })}
            </p>
            <p className="text-[0.7rem] text-fg-dim mt-0.5">
              {t('orderFeed.justNow')}
            </p>
          </div>
        </div>
        {/* Dismiss — full-height gutter with left border */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            toast.dismiss(toastId)
          }}
          className="flex items-center justify-center w-12 border-l border-border/30 bg-black/15 text-fg-muted hover:text-foreground hover:bg-black/25 transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      {/* Clickable content area */}
      <button
        type="button"
        onClick={handleClick}
        className="w-full text-left cursor-pointer hover:bg-foreground/[0.03] transition-colors"
      >
        {/* Divider — edge to edge */}
        <div className="border-t border-border/30" />

        {/* Content */}
        {isSingle && newOrders[0] ? (
          <SingleOrderContent order={newOrders[0]} fmt={fmt} />
        ) : (
          <MultiOrderContent
            orders={newOrders}
            totalRevenue={totalRevenue}
            fmt={fmt}
          />
        )}
      </button>
    </div>
  )
}

/* ------------------------------------------------------------------ */

function SingleOrderContent({
  order,
  fmt,
}: {
  order: OrderGroup
  fmt: (v: number) => string
}): ReactElement {
  const firstLine = order.lines[0]

  return (
    <div className="px-4 py-3 space-y-2">
      {/* Order ID + total */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-base leading-none">
            {codeToFlag(order.country)}
          </span>
          <span className="font-mono text-xs font-medium text-fg-muted">
            #{order.displayOrderId}
          </span>
        </div>
        <span className="font-mono text-sm font-bold text-emerald-400">
          {fmt(order.total)}
        </span>
      </div>

      {/* First product line */}
      {firstLine && (
        <div className="flex items-center gap-2.5">
          <img
            src={firstLine.imageUrl || FALLBACK_IMG}
            alt=""
            className="w-7 h-7 rounded-md object-cover bg-muted/30 shrink-0"
            loading="lazy"
            onError={(e) => {
              ;(e.target as HTMLImageElement).src = FALLBACK_IMG
            }}
          />
          <span className="text-xs text-fg-muted truncate flex-1">
            {firstLine.name}
          </span>
          {order.lines.length > 1 && (
            <span className="text-[0.65rem] text-fg-dim bg-foreground/5 px-1.5 py-0.5 rounded shrink-0">
              +{order.lines.length - 1} more
            </span>
          )}
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */

function MultiOrderContent({
  orders,
  totalRevenue,
  fmt,
}: {
  orders: OrderGroup[]
  totalRevenue: number
  fmt: (v: number) => string
}): ReactElement {
  return (
    <div className="px-4 py-3">
      {/* Revenue highlight */}
      <div className="font-mono text-lg font-bold text-emerald-400 mb-1.5">
        +{fmt(totalRevenue)}
      </div>

      {/* Order IDs summary */}
      <div className="flex items-center gap-1.5 flex-wrap text-xs text-fg-muted">
        {orders.slice(0, 3).map((o, i) => (
          <span key={o.orderId} className="flex items-center gap-1">
            {i > 0 && <span className="text-fg-dim">&middot;</span>}
            <span>{codeToFlag(o.country)}</span>
            <span className="font-mono">#{o.displayOrderId}</span>
          </span>
        ))}
        {orders.length > 3 && (
          <span className="text-fg-dim">&middot; +{orders.length - 3} more</span>
        )}
      </div>
    </div>
  )
}
